import { useState, useRef, useEffect, useCallback } from 'react'
import { resolveSessionCredentials, type StaffAuthResponse } from '../lib/staff-session'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import '../i18n'
import { API_URL, supabase } from '../lib/supabase'
import { config } from '../config'
import { onboardStaffPushNotifications } from '../services/staffPushOnboarding'

declare global {
  interface Window {
    initSendOTP?: (config: Record<string, unknown>) => void
    sendOtp?: (identifier: string, onSuccess?: (d: unknown) => void, onFailure?: (e: unknown) => void) => void
    verifyOtp?: (otp: string, onSuccess?: (d: unknown) => void, onFailure?: (e: unknown) => void, reqId?: string) => void
    retryOtp?: (channel: string | null, onSuccess?: (d: unknown) => void, onFailure?: (e: unknown) => void, reqId?: string) => void
  }
}

const MSG91_SCRIPT = 'https://verify.msg91.com/otp-provider.js'
const WIDGET_ID = config.msg91WidgetId
const WIDGET_TOKEN = config.msg91WidgetToken
const USE_MSG91_WIDGET = Boolean(WIDGET_ID && WIDGET_TOKEN)

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#0f1419',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'calc(24px + var(--safe-top, 0px)) 24px calc(24px + var(--safe-bottom, 0px))',
  },
  inner: {
    width: '100%',
    maxWidth: 400,
  },
  header: {
    textAlign: 'center',
    marginBottom: 40,
  },
  title: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 8,
  },
  subtitle: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 14,
  },
  inputWrap: {
    position: 'relative',
    marginBottom: 20,
  },
  phonePrefix: {
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'rgba(255,255,255,0.6)',
    fontSize: 16,
    fontWeight: 600,
    pointerEvents: 'none',
  },
  input: {
    width: '100%',
    height: 56,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12,
    color: '#fff',
    fontSize: 18,
    fontWeight: 600,
    paddingLeft: 56,
    paddingRight: 16,
    outline: 'none',
    boxSizing: 'border-box',
    letterSpacing: 2,
  },
  btn: {
    width: '100%',
    height: 56,
    borderRadius: 12,
    border: 'none',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.15s ease',
  },
  btnOrange: {
    background: '#ff6b35',
    color: '#fff',
  },
  btnGreen: {
    background: '#2ecc71',
    color: '#fff',
  },
  otpRow: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    marginBottom: 24,
  },
  otpInput: {
    width: 56,
    height: 64,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 12,
    color: '#fff',
    fontSize: 24,
    fontWeight: 700,
    textAlign: 'center',
    outline: 'none',
  },
  error: {
    color: '#ef4444',
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 16,
    padding: '10px 16px',
    background: 'rgba(239,68,68,0.1)',
    borderRadius: 8,
  },
  link: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 24,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    textDecoration: 'underline',
  },
  resend: {
    color: '#ff6b35',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 16,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
  },
  timer: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 16,
  },
  phoneDisplay: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 24,
  },
}

const errorKeyMap: Record<string, string> = {
  phone_not_found: 'login_phone_not_found',
  rate_limited: 'login_too_many',
  otp_expired: 'login_otp_expired',
  otp_invalid: 'login_otp_invalid',
  too_many_attempts: 'login_too_many',
  otp_send_failed: 'login_otp_send_failed',
}

export default function StaffLogin() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [step, setStep] = useState<'phone' | 'otp'>('phone')
  const [phone, setPhone] = useState('')
  const [digits, setDigits] = useState(['', '', '', ''])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [widgetReady, setWidgetReady] = useState(false)
  const [reqId, setReqId] = useState<string | null>(null)

  const otpRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (countdown <= 0) return
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown])

  useEffect(() => {
    if (!USE_MSG91_WIDGET) {
      setWidgetReady(true)
      return
    }
    if (document.querySelector(`script[src="${MSG91_SCRIPT}"]`)) {
      setWidgetReady(true)
      return
    }
    const script = document.createElement('script')
    script.src = MSG91_SCRIPT
    script.async = true
    script.onload = () => {
      const config: Record<string, unknown> = {
        widgetId: WIDGET_ID,
        tokenAuth: WIDGET_TOKEN,
        exposeMethods: true,
        success: () => {},
        failure: () => {},
      }
      if (typeof window.initSendOTP === 'function') {
        window.initSendOTP(config)
      }
      setWidgetReady(true)
    }
    document.body.appendChild(script)
    return () => {
      const el = document.querySelector(`script[src="${MSG91_SCRIPT}"]`)
      if (el) el.remove()
    }
  }, [])


  const handleSendOtp = async () => {
    if (phone.length !== 10) return
    setError('')
    setLoading(true)
    setReqId(null)
    try {
      if (USE_MSG91_WIDGET && widgetReady && typeof window.sendOtp === 'function') {
        const checkRes = await fetch(`${API_URL}/api/staff-auth/check-phone?phone=${encodeURIComponent(phone)}`)
        const checkJson = await checkRes.json()
        if (!checkRes.ok || !checkJson.data?.exists) {
          setError(String(t('login_phone_not_found')))
          setLoading(false)
          return
        }
        const identifier = '91' + phone
        window.sendOtp(
          identifier,
          (data: unknown) => {
            const d = data as Record<string, unknown>
            const id = (d?.request_id ?? d?.reqId ?? d?.requestId) as string | undefined
            if (id) setReqId(id)
            setStep('otp')
            setCountdown(30)
            setTimeout(() => otpRefs.current[0]?.focus(), 100)
            setLoading(false)
          },
          () => {
            setError(String(t('login_error')))
            setLoading(false)
          }
        )
        return
      }
      const res = await fetch(`${API_URL}/api/staff-auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      })
      const json = await res.json()
      if (!res.ok) {
        const key = errorKeyMap[json.error] || 'login_error'
        setError(String(t(key)))
        return
      }
      setStep('otp')
      setCountdown(30)
      setTimeout(() => otpRefs.current[0]?.focus(), 100)
    } catch {
      setError(String(t('login_error')))
    } finally {
      setLoading(false)
    }
  }

  const handleOtpChange = useCallback((index: number, value: string) => {
    if (!/^\d*$/.test(value)) return
    const next = [...digits]
    next[index] = value.slice(-1)
    setDigits(next)
    if (value && index < 3) {
      otpRefs.current[index + 1]?.focus()
    }
  }, [digits])

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
  }

  /**
   * Establishes the Supabase session from the staff-auth response.
   *
   * Prefers the server-minted tokens (access_token/refresh_token) via
   * setSession, which is immune to the browser supabase-js regression that made
   * verifyOtp({ token_hash }) 403 with a bogus otp_expired. Falls back to the
   * legacy token_hash exchange only if an older backend is still deployed.
   */
  const establishSession = async (data: StaffAuthResponse) => {
    const creds = resolveSessionCredentials(data)
    if (creds.mode === 'session') {
      const { error } = await supabase.auth.setSession({
        access_token: creds.accessToken,
        refresh_token: creds.refreshToken,
      })
      if (error) throw error
      return
    }
    if (creds.mode === 'token_hash') {
      const { error } = await supabase.auth.verifyOtp({ token_hash: creds.tokenHash, type: 'email' })
      if (error) throw error
      return
    }
    throw new Error('No session credentials in response')
  }

  const handleVerify = async () => {
    const otp = digits.join('')
    if (otp.length !== 4) return
    setError('')
    setLoading(true)
    try {
      if (USE_MSG91_WIDGET && widgetReady && typeof window.verifyOtp === 'function') {
        window.verifyOtp(
          otp,
          async (data: unknown) => {
            const d = data as Record<string, unknown>
            const accessToken = (d?.access_token ?? d?.['access-token'] ?? d?.token) as string | undefined
            if (!accessToken) {
              setError(String(t('login_error')))
              setLoading(false)
              return
            }
            const res = await fetch(`${API_URL}/api/staff-auth/verify-msg91-token`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ access_token: accessToken }),
            })
            const json = await res.json()
            if (!res.ok) {
              const key = errorKeyMap[json.error] || 'login_error'
              setError(String(t(key)))
              setLoading(false)
              return
            }
            await establishSession(json.data)
            void onboardStaffPushNotifications().catch(error => {
              console.warn('[StaffLogin] Push onboarding failed:', error)
            })
            navigate('/staff/home')
          },
          () => {
            setError(String(t('login_otp_invalid')))
            setLoading(false)
          },
          reqId ?? undefined
        )
      } else {
        const res = await fetch(`${API_URL}/api/staff-auth/verify-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone, otp }),
        })
        const json = await res.json()
        if (!res.ok) {
          const key = errorKeyMap[json.error] || 'login_error'
          setError(String(t(key)))
          setLoading(false)
          return
        }
        await establishSession(json.data)
        void onboardStaffPushNotifications().catch(error => {
          console.warn('[StaffLogin] Push onboarding failed:', error)
        })
        navigate('/staff/home')
      }
    } catch {
      setError(String(t('login_error')))
    } finally {
      setLoading(false)
    }
  }

  const handleResend = () => {
    setDigits(['', '', '', ''])
    setError('')
    if (USE_MSG91_WIDGET && widgetReady && typeof window.retryOtp === 'function') {
      setLoading(true)
      window.retryOtp(
        null,
        () => {
          setCountdown(30)
          setLoading(false)
        },
        () => {
          setError(String(t('login_error')))
          setLoading(false)
        },
        reqId ?? undefined
      )
    } else {
      handleSendOtp()
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.inner}>
      <div style={styles.header}>
        <div style={styles.title}>{t('staff_login_title', 'Staff Login')}</div>
        <div style={styles.subtitle}>
          {step === 'phone'
            ? t('staff_login_phone_subtitle', 'Enter your registered phone number')
            : t('staff_login_otp_subtitle', 'Enter the 4-digit code sent to your phone')}
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {step === 'phone' ? (
        <>
          <div style={styles.inputWrap}>
            <span style={styles.phonePrefix}>+91</span>
            <input
              style={styles.input}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              placeholder="9876543210"
              value={phone}
              onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
              autoFocus
            />
          </div>
          <button
            style={{
              ...styles.btn,
              ...styles.btnOrange,
              opacity: loading || phone.length !== 10 || (USE_MSG91_WIDGET && !widgetReady) ? 0.5 : 1,
            }}
            onClick={handleSendOtp}
            disabled={loading || phone.length !== 10 || (USE_MSG91_WIDGET && !widgetReady)}
          >
            {loading ? t('loading', 'Loading...') : t('get_otp', 'Get OTP')}
          </button>
        </>
      ) : (
        <>
          <div style={styles.phoneDisplay}>+91 {phone}</div>
          <div style={styles.otpRow}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={el => { otpRefs.current[i] = el }}
                style={styles.otpInput}
                type="tel"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={e => handleOtpChange(i, e.target.value)}
                onKeyDown={e => handleOtpKeyDown(i, e)}
              />
            ))}
          </div>
          <button
            style={{
              ...styles.btn,
              ...styles.btnGreen,
              opacity: loading || digits.join('').length !== 4 ? 0.5 : 1,
            }}
            onClick={handleVerify}
            disabled={loading || digits.join('').length !== 4}
          >
            {loading ? t('loading', 'Verifying...') : t('verify', 'Verify')}
          </button>
          {countdown > 0 ? (
            <div style={styles.timer}>
              {t('resend_in', 'Resend in')} {countdown}s
            </div>
          ) : (
            <button style={styles.resend} onClick={handleResend} disabled={loading}>
              {t('resend_otp', 'Resend OTP')}
            </button>
          )}
          <button
            style={{ ...styles.link, display: 'block', width: '100%' }}
            onClick={() => { setStep('phone'); setDigits(['', '', '', '']); setError('') }}
          >
            {t('change_phone', 'Change phone number')}
          </button>
        </>
      )}

      <button style={styles.link} onClick={() => navigate('/')}>
        {t('back', '← Back')}
      </button>
      </div>
    </div>
  )
}
